import {
  isMultiHierarchyAccountSummary,
  isMultiHierarchyRowGroupLike,
  MultiHierarchyAccountSummary,
  PublicMhDrillCapability
} from '@opengov/reporting-types';
import { RowNode } from 'ag-grid-community';
import {
  getGroupingValuesForMultiHierarchy,
  resolveDrillBreakdownLevel
} from '../editor/sidepanel/rowEditor/multigroupingUtils';

type DrillNodeData = {
  rowGroupIndex?: number;
  expandsToLeafDetail?: boolean;
};

/**
 * Builds the exact child-drill capability that expanding `node` will request on `/next/rows`.
 * Returns undefined when the node cannot produce a non-empty public MH drill (e.g. missing
 * row group, calculated row, or empty groupingValues).
 */
export function derivePublicMhDrillCapability(
  node: RowNode,
  accountSummary: MultiHierarchyAccountSummary | undefined
): PublicMhDrillCapability | undefined {
  if (!accountSummary || !isMultiHierarchyAccountSummary(accountSummary)) {
    return undefined;
  }

  const data = node.data as DrillNodeData | undefined;
  if (data?.rowGroupIndex === undefined) return undefined;

  const rowGroup = accountSummary.rowGroups[data.rowGroupIndex];
  if (!isMultiHierarchyRowGroupLike(rowGroup) || !rowGroup.id) return undefined;

  const groupingValues = getGroupingValuesForMultiHierarchy(node, rowGroup);
  if (groupingValues.length === 0) return undefined;

  const expandLeafDetail = data.expandsToLeafDetail === true;
  return {
    rowGroupId: rowGroup.id,
    groupingValues,
    rowBreakdownLevel: resolveDrillBreakdownLevel(node),
    ...(expandLeafDetail ? { expandLeafDetail: true as const } : {})
  };
}
